import type { LogLevel } from '@/lib/logger';

const getMonotonicTimeMs = (): number => {
  if (performance !== undefined && typeof performance.now === 'function') {
    return performance.now();
  }

  if (process !== undefined && typeof process.hrtime === 'function') {
    const [seconds, nanoseconds] = process.hrtime();
    return seconds * 1000 + nanoseconds / 1_000_000;
  }

  return 0;
};

type Logger = {
  info: (message: string, data?: Record<string, unknown>) => void;
  debug: (message: string, data?: Record<string, unknown>) => void;
  warn: (message: string, data?: Record<string, unknown>) => void;
  error: (message: string, data?: Record<string, unknown>) => void;
};

type WithDurationOptions<T> = {
  logger: Logger;
  level: LogLevel | 'trace';
  message: string;
  meta: Record<string, unknown>;
  function_: () => Promise<T> | T;
};

export async function withDuration<T>({ logger, level, message, meta, function_ }: WithDurationOptions<T>): Promise<T> {
  const start = getMonotonicTimeMs();
  const base = { ...meta };
  if (level === 'debug' || level === 'trace') {
    logger.debug(`${message}:start`, base);
  } else {
    logger.info(`${message}:start`, base);
  }
  try {
    const result = await function_();
    const durationMs = Math.max(0, getMonotonicTimeMs() - start);
    const done = { ...base, durationMs } as Record<string, unknown>;
    if (level === 'debug' || level === 'trace') {
      logger.debug(`${message}:done`, done);
    } else {
      logger.info(`${message}:done`, done);
    }
    return result;
  } catch (error) {
    const durationMs = Math.max(0, getMonotonicTimeMs() - start);
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;
    logger.error(`${message}:error`, { ...base, durationMs, error: errorMessage, stack: errorStack });
    throw error;
  }
}

export function logStep(logger: Logger, step: string, data?: Record<string, unknown>): void {
  logger.debug(`step:${step}`, data);
}

function getConsole(): Pick<Console, 'info' | 'debug' | 'warn' | 'error'> | undefined {
  try {
    const g = globalThis as { console?: Console };
    const instance = g.console;
    if (!instance) {
      return;
    }

    // Bind methods so `this` is the original console instance (Safari can require this).
    return {
      info: (instance.info ?? instance.log).bind(instance),
      debug: (instance.debug ?? instance.log).bind(instance),
      warn: (instance.warn ?? instance.log).bind(instance),
      error: (instance.error ?? instance.log).bind(instance),
    };
  } catch {
    // Console unavailable (non-browser runtime); ignore.
    return;
  }
}

function makeConsole(method: 'info' | 'debug' | 'warn' | 'error') {
  const methodName: 'info' | 'debug' | 'warn' | 'error' = method;
  return (message: string, data?: unknown, module = 'client') => {
    try {
      const c = getConsole();
      // Use bracket access to avoid static console references for linters
      c?.[methodName](`[${module}] ${message}${data === undefined ? '' : ' '}`, data);
    } catch {
      // Intentionally swallow logging failures to avoid secondary errors.
    }
  };
}

// Lightweight client/server-safe logging helpers for UI code
export const logInfo = makeConsole('info');
export const logDebug = makeConsole('debug');
export const logWarning = makeConsole('warn');
export const logError = makeConsole('error');
